import { Badge } from "@shared/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  AlertCircle,
  CheckCircle,
  FileText,
  MapPin,
  Truck,
  User,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { GenericTable } from "../../../../../shared/components/GenericTable";
import { usePaperBillList } from "../../hooks";

// Mock data for in-house employees (you can replace this with an API call)
const inHouseEmployees = [
  {
    id: "1",
    name: "John Smith",
    employeeId: "EMP001",
    designation: "Field Executive",
    capacity: 200,
    assigned: 120,
  },
  {
    id: "2",
    name: "Alice Johnson",
    employeeId: "EMP002",
    designation: "Meter Reader",
    capacity: 150,
    assigned: 105,
  },
  {
    id: "3",
    name: "Mike Wilson",
    employeeId: "EMP003",
    designation: "Field Officer",
    capacity: 180,
    assigned: 0,
  },
  {
    id: "4",
    name: "Sarah Davis",
    employeeId: "EMP004",
    designation: "Distribution Executive",
    capacity: 220,
    assigned: 0,
  },
];
interface SubAreaData {
  subAreaCode: string;
  area: string;
  subArea: string;
  totalPremiseCount: number;
  totalBillCount: number;
  assignedToName: string;
  premiseCount: number;
  status: number;
  updatedBy: string;
  updatedOn: string;
  billRuns: number[];
}

interface PaperBillDistributionProps {
  cycleId: string;
}

export function PaperBillDistribution({ cycleId }: PaperBillDistributionProps) {
  const params = useParams();
  const { data: billListData, isLoading: billListLoading } = usePaperBillList(
    params.id
  );

  const totalCount = billListData?.count;

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const startItem = (billListData?.pageNumber - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(billListData?.pageNumber * PAGE_SIZE, totalCount);

  const getStatusBadge = (status: number) => {
    switch (status) {
      case 0:
        return (
          <Badge variant="secondary">
            <AlertCircle className="w-3 h-3 mr-1" /> Unassigned
          </Badge>
        );
      case 1:
        return (
          <Badge variant="outline">
            <User className="w-3 h-3 mr-1" /> Assigned (In-house)
          </Badge>
        );
      case 2:
        return (
          <Badge variant="outline">
            <Truck className="w-3 h-3 mr-1" /> Assigned (External)
          </Badge>
        );
      case 3:
        return (
          <Badge
            variant="outline"
            className="bg-green-100 text-green-800 border-green-200"
          >
            <CheckCircle className="w-3 h-3 mr-1" /> Completed
          </Badge>
        );
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  const columns = [
    {
      key: "area",
      header: "Area",
      render: (value: any, row: SubAreaData) => (
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          <div>
            <div className="font-medium">{row.area}</div>
            <div className="text-xs text-muted-foreground">
              {row.totalPremiseCount} premises • {row.totalBillCount} bills
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "subArea",
      header: "Sub-Area",
      render: (value: string) => <span className="font-medium">{value}</span>,
    },
    {
      key: "premiseCount",
      header: "Premises",
    },
    {
      key: "totalBillCount",
      header: "Bills",
    },
    {
      key: "status",
      header: "Status",
      render: (value: number) => getStatusBadge(value),
    },
    {
      key: "assignedToName",
      header: "Assigned To",
    },

    {
      key: "updatedBy",
      header: "Updated By",
    },
    {
      key: "updatedOn",
      header: "Updated On",
    },
  ];

  const paginationConfig = {
    currentPage: billListData?.pageNumber,
    totalPages,
    totalItems: totalCount,
    itemsPerPage: PAGE_SIZE,
    startItem,
    endItem,
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Paper Bill Distribution Dashboard
          </CardTitle>
          <CardDescription>
            Overview of paper bill distribution across all areas and sub-areas
            with assignment and progress tracking.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Dashboard Metrics */}

          <GenericTable
            data={billListData?.result ?? []}
            columns={columns}
            rowKey="subAreaCode"
            emptyMessage="No distribution data found"
            onPageChange={function (page: number): void {
              throw new Error("Function not implemented.");
            }}
            pagination={paginationConfig}
          />
        </CardContent>
      </Card>
    </div>
  );
}
